import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Conversation, ConversationDetailResponse } from '../services/api';

export interface ClientAuth {
  instanceUrl: string;
  sessionId: string;
  apiVersion: string;
  username?: string;
  orgName?: string;
}

export interface Workspace {
	workspace_id: string;
	org_id: string;
	connection_id: string;
	owner_id: string | null;
	snapshot_id: string | null;
	container_app_id: string | null;
	container_app_url: string | null;
	file_share_name: string | null;
	infra_status: 'pending' | 'running' | 'completed' | 'failed';
	infra_started_at: string | null;
	infra_completed_at: string | null;
	infra_error_info: string | null;
	snapshot_apply_warnings: string | null; // JSON: { skipped_count: number; skipped_files: string[] }
	name: string;
	type: string;
	created_at: string;
	last_selected_at: string | null;
	connection: {
		connection_id: string;
		label: string;
		sf_org_id: string;
		sf_username: string;
		sf_instance_url: string;
		is_sandbox: boolean;
		created_at: string;
		last_used_at: string | null;
		last_selected_at: string | null;
	};
}

export interface AppState {
  // Auth state
  clientAuth: ClientAuth | null;
  orgName: string | null;
  containerAppUrl: string | null;
	activeWorkspaceId: string | null;
  workspaces: Workspace[];
  
  // UI state
  currentSection: 'auth' | 'knowledge' | 'conversation';
  isLoading: boolean;
  error: string | null;
  
  // Prompt templates
  promptTemplates: string[];
  
  // Progress state
  progress: number;
  progressMessage: string;
  showProgress: boolean;
  
  // Feedback state
  lastPromptId: string | null;
  lastPromptContent: string | null;
  lastResponseId: string | null;
  lastResponseContent: string | null;
  feedbackSubmitted: boolean;
  
  // Conversation state
  currentConversationId: string | null;
  currentResponseId: string | null;
  showAllItems: boolean;
  conversations: Conversation[];
  conversationsLoading: boolean;
  conversationsHasMore: boolean;
  conversationsCursor: { timestamp: number; conversation_id: string } | null;
  currentConversationDetail: ConversationDetailResponse | null;
  
  // View state
  currentView: 'conversation' | 'insights' | 'metadata' | 'analysis' | 'documents';
  selectedMetadataPath: string;
}

export interface AppActionsContextType {
  // Auth actions
  setClientAuth: (auth: ClientAuth | null) => void;
  setOrgName: (name: string | null) => void;
  setContainerAppUrl: (url: string | null) => void;
	setActiveWorkspaceId: (id: string | null) => void;
  loadWorkspaces: () => Promise<Workspace[]>;
  
  // UI actions
  setCurrentSection: (section: 'auth' | 'knowledge' | 'conversation') => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Prompt template actions
  setPromptTemplates: (templates: string[]) => void;
  
  // Progress actions
  setProgress: (progress: number) => void;
  setProgressMessage: (message: string) => void;
  setShowProgress: (show: boolean) => void;
  
  // Feedback actions
  setFeedbackData: (data: {
    lastPromptId: string | null;
    lastPromptContent: string | null;
    lastResponseId: string | null;
    lastResponseContent: string | null;
  }) => void;
  setFeedbackSubmitted: (submitted: boolean) => void;
  
  // Conversation actions
  setCurrentConversationId: (id: string | null) => void;
  setCurrentResponseId: (id: string | null) => void;
  setShowAllItems: (show: boolean) => void;
  setConversations: (conversations: Conversation[]) => void;
  addConversations: (conversations: Conversation[]) => void;
  prependConversation: (conversation: Conversation) => void;
  updateConversationInList: (conversation: Conversation) => void;
  removeConversation: (conversationId: string) => void;
  setConversationsLoading: (loading: boolean) => void;
  setConversationsHasMore: (hasMore: boolean) => void;
  setConversationsCursor: (cursor: { timestamp: number; conversation_id: string } | null) => void;
  setCurrentConversationDetail: (
    detail: ConversationDetailResponse | null | ((prev: ConversationDetailResponse | null) => ConversationDetailResponse | null)
  ) => void;
  
  // View actions
  setCurrentView: (view: 'conversation' | 'insights' | 'metadata' | 'analysis' | 'documents') => void;
  setSelectedMetadataPath: (path: string) => void;
  
  // Utility actions
  clearError: () => void;
  resetApp: () => void;
}

// Legacy combined interface for backward compatibility
export interface AppContextType extends AppState, AppActionsContextType {}

// Separate contexts for better performance
// Components that only need actions won't re-render when state changes
// Components that only need state won't re-render when actions change
const AppStateContext = createContext<AppState | undefined>(undefined);
const AppActionsContext = createContext<AppActionsContextType | undefined>(undefined);

// Legacy combined context for backward compatibility
const AppContext = createContext<AppContextType | undefined>(undefined);

// Hooks for accessing specific contexts
export const useAppState = (): AppState => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
};

export const useAppActions = (): AppActionsContextType => {
  const context = useContext(AppActionsContext);
  if (!context) {
    throw new Error('useAppActions must be used within an AppProvider');
  }
  return context;
};

// Legacy hook for backward compatibility
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, setState] = useState<AppState>({
    clientAuth: null,
    orgName: null,
    containerAppUrl: null,
		activeWorkspaceId: null,
    workspaces: [],
    currentSection: 'auth',
    isLoading: false,
    error: null,
    promptTemplates: [],
    progress: 0,
    progressMessage: '',
    showProgress: false,
    lastPromptId: null,
    lastPromptContent: null,
    lastResponseId: null,
    lastResponseContent: null,
    feedbackSubmitted: false,
    currentConversationId: null,
    currentResponseId: null,
    showAllItems: false,
    conversations: [],
    conversationsLoading: false,
    conversationsHasMore: false,
    conversationsCursor: null,
    currentConversationDetail: null,
    currentView: 'conversation',
    selectedMetadataPath: 'tooling-data/installed-packages.json',
  });

  const setClientAuth = useCallback((auth: ClientAuth | null) => {
    setState(prev => ({ ...prev, clientAuth: auth }));
  }, []);

  const setOrgName = useCallback((name: string | null) => {
    setState(prev => ({ ...prev, orgName: name }));
  }, []);

  const setContainerAppUrl = useCallback((url: string | null) => {
    setState(prev => ({ ...prev, containerAppUrl: url }));
  }, []);

	const setActiveWorkspaceId = useCallback((id: string | null) => {
		setState(prev => ({ ...prev, activeWorkspaceId: id }));
	}, []);

  const loadWorkspaces = useCallback(async (): Promise<Workspace[]> => {
    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getWorkspaces();
      if (response.success && response.data) {
        const workspaces = response.data!.workspaces || [];
        setState(prev => ({ ...prev, workspaces }));
        return workspaces;
      }
      return [];
    } catch (error) {
      console.error('Failed to load workspaces:', error);
      return [];
    }
  }, []);

  const setCurrentSection = useCallback((section: 'auth' | 'knowledge' | 'conversation') => {
    setState(prev => ({ ...prev, currentSection: section }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const setPromptTemplates = useCallback((templates: string[]) => {
    setState(prev => ({ ...prev, promptTemplates: templates }));
  }, []);

  const setProgress = useCallback((progress: number) => {
    setState(prev => ({ ...prev, progress }));
  }, []);

  const setProgressMessage = useCallback((message: string) => {
    setState(prev => ({ ...prev, progressMessage: message }));
  }, []);

  const setShowProgress = useCallback((show: boolean) => {
    setState(prev => ({ ...prev, showProgress: show }));
  }, []);

  const setFeedbackData = useCallback((data: {
    lastPromptId: string | null;
    lastPromptContent: string | null;
    lastResponseId: string | null;
    lastResponseContent: string | null;
  }) => {
    setState(prev => ({ ...prev, ...data }));
  }, []);

  const setFeedbackSubmitted = useCallback((submitted: boolean) => {
    setState(prev => ({ ...prev, feedbackSubmitted: submitted }));
  }, []);

  const setCurrentConversationId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, currentConversationId: id }));
  }, []);

  const setCurrentResponseId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, currentResponseId: id }));
  }, []);

  const setShowAllItems = useCallback((show: boolean) => {
    setState(prev => ({ ...prev, showAllItems: show }));
  }, []);

  const setConversations = useCallback((conversations: Conversation[]) => {
    setState(prev => ({ ...prev, conversations }));
  }, []);

  const addConversations = useCallback((conversations: Conversation[]) => {
    setState(prev => ({ ...prev, conversations: [...prev.conversations, ...conversations] }));
  }, []);

  const prependConversation = useCallback((conversation: Conversation) => {
    setState(prev => {
      // Check if conversation already exists
      const existingIndex = prev.conversations.findIndex(c => c.id === conversation.id);
      if (existingIndex !== -1) {
        // Move existing conversation to top, preserving original created_at but updating updated_at
        const existingConversation = prev.conversations[existingIndex];
        const updatedConversation: Conversation = {
          ...conversation,
          created_at: existingConversation.created_at, // Preserve original created_at
        };
        const updatedConversations = [...prev.conversations];
        updatedConversations.splice(existingIndex, 1);
        return { ...prev, conversations: [updatedConversation, ...updatedConversations] };
      }
      // Add new conversation to top
      return { ...prev, conversations: [conversation, ...prev.conversations] };
    });
  }, []);

  const updateConversationInList = useCallback((conversation: Conversation) => {
    setState(prev => {
      const existingIndex = prev.conversations.findIndex(c => c.id === conversation.id);
      if (existingIndex !== -1) {
        // Update existing conversation in place, preserving its position
        const updatedConversations = [...prev.conversations];
        const existingConversation = updatedConversations[existingIndex];
        updatedConversations[existingIndex] = {
          ...existingConversation,
          ...conversation,
          // Preserve original created_at if not provided in update
          created_at: conversation.created_at ?? existingConversation.created_at,
        };
        return { ...prev, conversations: updatedConversations };
      }
      // Conversation not found in list, no-op
      return prev;
    });
  }, []);

  const removeConversation = useCallback((conversationId: string) => {
    setState(prev => ({
      ...prev,
      conversations: prev.conversations.filter(c => c.id !== conversationId),
    }));
  }, []);

  const setConversationsLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, conversationsLoading: loading }));
  }, []);

  const setConversationsHasMore = useCallback((hasMore: boolean) => {
    setState(prev => ({ ...prev, conversationsHasMore: hasMore }));
  }, []);

  const setConversationsCursor = useCallback((cursor: { timestamp: number; conversation_id: string } | null) => {
    setState(prev => ({ ...prev, conversationsCursor: cursor }));
  }, []);

  const setCurrentConversationDetail = useCallback((
    detail: ConversationDetailResponse | null | ((prev: ConversationDetailResponse | null) => ConversationDetailResponse | null)
  ) => {
    setState(prev => {
      const nextDetail = typeof detail === 'function' ? detail(prev.currentConversationDetail) : detail;
      return { ...prev, currentConversationDetail: nextDetail };
    });
  }, []);

  const setCurrentView = useCallback((view: 'conversation' | 'insights' | 'metadata' | 'analysis' | 'documents') => {
    setState(prev => ({ ...prev, currentView: view }));
  }, []);

  const setSelectedMetadataPath = useCallback((path: string) => {
    setState(prev => ({ ...prev, selectedMetadataPath: path }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const resetApp = useCallback(() => {
    setState({
      clientAuth: null,
      orgName: null,
      containerAppUrl: null,
			activeWorkspaceId: null,
      workspaces: [],
      currentSection: 'auth',
      isLoading: false,
      error: null,
      promptTemplates: [],
      progress: 0,
      progressMessage: '',
      showProgress: false,
      lastPromptId: null,
      lastPromptContent: null,
      lastResponseId: null,
      lastResponseContent: null,
      feedbackSubmitted: false,
      currentConversationId: null,
      currentResponseId: null,
      showAllItems: false,
      conversations: [],
      conversationsLoading: false,
      conversationsHasMore: false,
      conversationsCursor: null,
      currentConversationDetail: null,
      currentView: 'conversation',
      selectedMetadataPath: 'tooling-data/installed-packages.json',
    });
  }, []);

  // Memoize the actions object to prevent unnecessary re-renders
  const actionsValue = useMemo((): AppActionsContextType => ({
    setClientAuth,
    setOrgName,
    setContainerAppUrl,
		setActiveWorkspaceId,
    loadWorkspaces,
    setCurrentSection,
    setLoading,
    setError,
    setPromptTemplates,
    setProgress,
    setProgressMessage,
    setShowProgress,
    setFeedbackData,
    setFeedbackSubmitted,
    setCurrentConversationId,
    setCurrentResponseId,
    setShowAllItems,
    setConversations,
    addConversations,
    prependConversation,
    updateConversationInList,
    removeConversation,
    setConversationsLoading,
    setConversationsHasMore,
    setConversationsCursor,
    setCurrentConversationDetail,
    setCurrentView,
    setSelectedMetadataPath,
    clearError,
    resetApp,
  }), [
    setClientAuth,
    setOrgName,
    setContainerAppUrl,
		setActiveWorkspaceId,
    loadWorkspaces,
    setCurrentSection,
    setLoading,
    setError,
    setPromptTemplates,
    setProgress,
    setProgressMessage,
    setShowProgress,
    setFeedbackData,
    setFeedbackSubmitted,
    setCurrentConversationId,
    setCurrentResponseId,
    setShowAllItems,
    setConversations,
    addConversations,
    prependConversation,
    updateConversationInList,
    removeConversation,
    setConversationsLoading,
    setConversationsHasMore,
    setConversationsCursor,
    setCurrentConversationDetail,
    setCurrentView,
    setSelectedMetadataPath,
    clearError,
    resetApp,
  ]);

  // Memoize the state object
  const stateValue = useMemo(() => state, [state]);

  // Memoize the legacy combined context value for backward compatibility
  const legacyContextValue = useMemo((): AppContextType => ({
    ...state,
    ...actionsValue,
  }), [state, actionsValue]);

  return (
    <AppStateContext.Provider value={stateValue}>
      <AppActionsContext.Provider value={actionsValue}>
        <AppContext.Provider value={legacyContextValue}>
          {children}
        </AppContext.Provider>
      </AppActionsContext.Provider>
    </AppStateContext.Provider>
  );
};
